// settings/pages/ProcurementSettingsPage.tsx
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import * as api from '../services/settingsApi';
import {
 Truck, ArrowLeft, Save, CheckCircle2, Sparkles, ChevronRight,
 ChevronLeft, AlertTriangle, Download, RefreshCw, ClipboardList,
 FileCheck, Users, ShieldCheck, Receipt
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// ============================================================
// TOGGLE SWITCH COMPONENT (same as inventory)
// ============================================================
const ToggleSwitch = ({ label, description, checked, onChange }: {
 label: string; description: string; checked: boolean; onChange: (v: boolean) => void;
}) => (
 <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 :bg-slate-700/50 transition-colors">
 <div className="flex-1">
 <p className="text-sm font-semibold text-slate-800 ">{label}</p>
 <p className="text-xs text-slate-500 mt-0.5">{description}</p>
 </div>
 <button type="button" onClick={() => onChange(!checked)}
 className={`relative w-12 h-7 rounded-full transition-colors duration-200 ${checked ? 'bg-emerald-500' : 'bg-slate-300 '}`}>
 <motion.div animate={{ x: checked ? 22 : 2 }} transition={{ type: "spring", stiffness: 500, damping: 30 }}
 className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-md" />
 </button>
 </div>
);

// ============================================================
// NUMBER INPUT FIELD (same as inventory)
// ============================================================
const NumberField = ({ label, value, onChange, icon: Icon, helperText, prefix }: {
 label: string; value: string; onChange: (v: string) => void; icon: React.ElementType; helperText?: string; prefix?: string;
}) => {
 const [isFocused, setIsFocused] = useState(false);
 const id = `num-${label.toLowerCase().replace(/\s+/g, '-')}`;
 return (
 <div>
 <label htmlFor={id} className={`block text-xs font-semibold mb-1.5 transition-colors duration-200 ${isFocused ? 'text-emerald-600' : 'text-slate-700'}`}>{label}</label>
 <div className={`relative rounded-xl border-2 transition-all duration-200 overflow-hidden ${isFocused ? 'border-emerald-400 shadow-lg shadow-emerald-500/10' : 'border-slate-200 hover:border-slate-300'} bg-white `}>
 <div className="flex items-center">
 {Icon && <div className={`pl-4 flex-shrink-0 transition-colors duration-200 ${isFocused ? 'text-emerald-500' : 'text-slate-400'}`}><Icon className="w-5 h-5" /></div>}
 {prefix && <span className="pl-3 text-sm text-slate-500 font-medium">{prefix}</span>}
 <input id={id} type="number" value={value} onChange={(e) => onChange(e.target.value)}
 onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
 className="flex-1 px-3 py-3 text-sm bg-transparent placeholder:text-slate-400 focus:outline-none text-slate-900 " />
 </div>
 </div>
 {helperText && <p className="text-xs text-slate-400 mt-1.5 px-1">{helperText}</p>}
 </div>
 );
};

// ============================================================
// STEP CONFIGURATION
// ============================================================
const STEPS = [
 { num: 1, title: 'Requisitions', icon: ClipboardList, description: 'Purchase requisition rules' },
 { num: 2, title: 'Purchase Orders', icon: FileCheck, description: 'PO approval thresholds' },
 { num: 3, title: 'Receiving', icon: Truck, description: 'Goods receiving rules' },
];

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ProcurementSettingsPage() {
 const navigate = useNavigate();
 const user = useAuthStore((s) => s.user);
 const queryClient = useQueryClient();
 const addToast = useToastStore((s) => s.addToast);

 const [currentStep, setCurrentStep] = useState(1);
 const [isDirty, setIsDirty] = useState(false);
 const [saving, setSaving] = useState(false);
 const [lastSaved, setLastSaved] = useState<Date | null>(null);
 const [completionStatus, setCompletionStatus] = useState<Record<number, boolean>>({});

 const [form, setForm] = useState({
 requireRequisition: true,
 autoApproveRequisition: false,
 requisitionApproverCount: '1',
 poAutoApprove: '100000',
 poManagerApprove: '500000',
 poDirectorApprove: '1000000',
 allowGrnWithoutPO: false,
 autoConfirmGrn: true,
 requireGrnApproval: true,
 allowPartialGrn: true,
 });

 const { isLoading } = useQuery({
 queryKey: ['tenant', user?.tenantId],
 queryFn: async () => {
 const t = await api.getTenant(user!.tenantId);
 const s = t.settings?.procurement || {};
 setForm({
 requireRequisition: s.requireRequisition !== false,
 autoApproveRequisition: s.autoApproveRequisition || false,
 requisitionApproverCount: String(s.requisitionApproverCount || 1),
 poAutoApprove: String(s.approvalThresholds?.poAutoApprove || 100000),
 poManagerApprove: String(s.approvalThresholds?.poManagerApprove || 500000),
 poDirectorApprove: String(s.approvalThresholds?.poDirectorApprove || 1000000),
 allowGrnWithoutPO: s.allowGrnWithoutPO || false,
 autoConfirmGrn: s.autoConfirmGrn !== false,
 requireGrnApproval: s.requireGrnApproval !== false,
 allowPartialGrn: s.allowPartialGrn !== false,
 });
 return t;
 },
 enabled: !!user?.tenantId,
 });

 const saveMutation = useMutation({
 mutationFn: () => api.updateTenant(user!.tenantId, {
 settings: {
 procurement: {
 requireRequisition: form.requireRequisition,
 autoApproveRequisition: form.autoApproveRequisition,
 requisitionApproverCount: parseInt(form.requisitionApproverCount),
 approvalThresholds: {
 poAutoApprove: parseInt(form.poAutoApprove),
 poManagerApprove: parseInt(form.poManagerApprove),
 poDirectorApprove: parseInt(form.poDirectorApprove),
 },
 allowGrnWithoutPO: form.allowGrnWithoutPO,
 autoConfirmGrn: form.autoConfirmGrn,
 requireGrnApproval: form.requireGrnApproval,
 allowPartialGrn: form.allowPartialGrn,
 },
 },
 }),
 onSuccess: () => {
 queryClient.invalidateQueries({ queryKey: ['tenant'] });
 setIsDirty(false);
 setLastSaved(new Date());
 setCompletionStatus(prev => ({ ...prev, [currentStep]: true }));
 addToast({ type: 'success', title: 'Saved', message: 'Procurement settings updated.' });
 },
 onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
 onSettled: () => setSaving(false),
 });

 const handleToggle = useCallback((field: string, value: boolean) => { setForm(prev => ({ ...prev, [field]: value })); setIsDirty(true); }, []);
 const handleChange = useCallback((field: string, value: string) => { setForm(prev => ({ ...prev, [field]: value })); setIsDirty(true); }, []);
 const handleSave = () => { setSaving(true); saveMutation.mutate(); };

 const getCompletedCount = () => { let count = 0; for (let i = 1; i <= STEPS.length; i++) { if (completionStatus[i]) count++; } return count; };

 if (isLoading) {
 return (
 <div className="flex items-center justify-center min-h-[80vh]">
 <div className="text-center">
 <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
 className="w-16 h-16 rounded-2xl border-4 border-emerald-200 border-t-emerald-600 mx-auto mb-6" />
 <p className="text-slate-500 font-medium">Loading procurement settings...</p>
 </div>
 </div>
 );
 }

 const completedCount = getCompletedCount();

 return (
 <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
 <div className="max-w-[1440px] mx-auto p-4 lg:p-6 space-y-6">

 {/* ========== GRADIENT HERO HEADER ========== */}
 <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
 className="relative overflow-hidden bg-gradient-to-br from-rose-600 via-pink-600 to-rose-700 rounded-3xl shadow-2xl">
 <div className="absolute inset-0 opacity-20">
 <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.3),transparent_50%)]" />
 <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
 </div>
 <div className="relative px-6 lg:px-8 py-6 lg:py-8">
 <div className="flex items-center justify-between mb-6">
 <button onClick={() => navigate('/settings/organization')}
 className="flex items-center gap-2 text-rose-100 hover:text-white transition-colors text-sm font-medium">
 <ArrowLeft className="w-4 h-4" /> Back to Overview
 </button>
 <div className="flex items-center gap-3">
 {lastSaved && <div className="flex items-center gap-2 text-xs text-rose-100"><Sparkles className="w-3.5 h-3.5" /> Saved {lastSaved.toLocaleTimeString()}</div>}
 {isDirty && <span className="text-xs text-rose-300 bg-rose-500/20 px-3 py-1 rounded-lg font-medium flex items-center gap-1"><AlertTriangle size={12} /> Unsaved</span>}
 <button onClick={() => navigate('/settings/organization')} className="px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 transition-all text-sm font-medium border border-white/20">Cancel</button>
 <button onClick={handleSave} disabled={saving || !isDirty} className="px-5 py-2 bg-white text-rose-700 rounded-xl hover:bg-gray-50 transition-all font-semibold text-sm shadow-lg flex items-center gap-2 disabled:opacity-50">
 {saving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Saving...</> : <><Download className="w-4 h-4" /> Save</>}
 </button>
 </div>
 </div>
 <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
 <motion.div whileHover={{ scale: 1.02 }} className="relative flex-shrink-0">
 <div className="w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-white/30 to-white/10 rounded-2xl flex items-center justify-center shadow-2xl ring-4 ring-white/20 backdrop-blur-sm">
 <ClipboardList className="w-10 h-10 lg:w-12 lg:h-12 text-white" />
 </div>
 </motion.div>
 <div className="flex-1 min-w-0">
 <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">Procurement Settings</h1>
 <p className="text-rose-100 text-sm mt-1">Configure requisitions, purchase orders, and receiving rules</p>
 </div>
 <div className="flex gap-4 flex-shrink-0">
 <div className="text-center px-5 py-3 bg-white/10 backdrop-blur-sm rounded-2xl"><p className="text-2xl font-bold text-white">{currentStep}/{STEPS.length}</p><p className="text-xs text-rose-200">Step</p></div>
 <div className="text-center px-5 py-3 bg-white/10 backdrop-blur-sm rounded-2xl"><p className="text-2xl font-bold text-white">{completedCount}/{STEPS.length}</p><p className="text-xs text-rose-200">Done</p></div>
 </div>
 </div>
 </div>
 </motion.div>

 {/* ========== MAIN CONTENT ========== */}
 <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
 <div className="lg:col-span-1">
 <div className="sticky top-24">
 <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
 <div className="px-5 py-4 border-b border-slate-100 "><h3 className="font-semibold text-slate-800 text-sm">Form Progress</h3></div>
 <div className="p-3 space-y-1">
 {STEPS.map((step) => {
 const isActive = currentStep === step.num;
 const isCompleted = completionStatus[step.num] || currentStep > step.num;
 return (
 <button key={step.num} onClick={() => setCurrentStep(step.num)}
 className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${isActive ? 'bg-gradient-to-r from-rose-50 to-pink-50 border-l-4 border-rose-500' : 'hover:bg-slate-50 border-l-4 border-transparent'}`}>
 <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 ${isActive ? 'bg-gradient-to-br from-rose-500 to-pink-600 text-white shadow-lg shadow-rose-500/25' : isCompleted ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-400'}`}>
 {isCompleted && !isActive ? <CheckCircle2 className="w-4 h-4" /> : <step.icon className="w-4 h-4" />}
 </div>
 <div className="flex-1 text-left min-w-0">
 <p className={`text-sm font-medium truncate ${isActive ? 'text-rose-700' : 'text-slate-800'}`}>{step.title}</p>
 <p className="text-[10px] text-slate-400 truncate">{step.description}</p>
 </div>
 </button>
 );
 })}
 </div>
 </div>
 </div>
 </div>

 <div className="lg:col-span-3">
 <AnimatePresence mode="wait">
 {currentStep === 1 && (
 <motion.div key="step1" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
 <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
 <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-rose-50/30 to-white ">
 <div className="flex items-center gap-3">
 <div className="p-2 rounded-xl bg-gradient-to-br from-rose-50 to-pink-50 ring-1 ring-rose-200"><ClipboardList className="w-5 h-5 text-rose-600" /></div>
 <div><h3 className="font-semibold text-slate-800">Purchase Requisitions</h3><p className="text-xs text-slate-500">Requisition creation and approval rules</p></div>
 </div>
 </div>
 <div className="p-6 space-y-3">
 <ToggleSwitch label="Require Purchase Requisition" description="Staff must submit requisition before PO creation" checked={form.requireRequisition} onChange={(v) => handleToggle('requireRequisition', v)} />
 <ToggleSwitch label="Auto-Approve Requisitions" description="Skip approval for requisitions below threshold" checked={form.autoApproveRequisition} onChange={(v) => handleToggle('autoApproveRequisition', v)} />
 <NumberField label="Number of Approvers Required" value={form.requisitionApproverCount} onChange={(v) => handleChange('requisitionApproverCount', v)} icon={Users} helperText="How many managers must approve a requisition" />
 </div>
 </div>
 </motion.div>
 )}

 {currentStep === 2 && (
 <motion.div key="step2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
 <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
 <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-pink-50/30 to-white ">
 <div className="flex items-center gap-3">
 <div className="p-2 rounded-xl bg-gradient-to-br from-pink-50 to-rose-50 ring-1 ring-pink-200"><FileCheck className="w-5 h-5 text-pink-600" /></div>
 <div><h3 className="font-semibold text-slate-800">Purchase Orders</h3><p className="text-xs text-slate-500">PO approval thresholds and rules</p></div>
 </div>
 </div>
 <div className="p-6 space-y-4">
 <NumberField label="Auto-Approve PO Below (₦)" value={form.poAutoApprove} onChange={(v) => handleChange('poAutoApprove', v)} icon={CheckCircle2} prefix="₦" helperText="POs below this amount are auto-approved" />
 <NumberField label="Manager Approval Up To (₦)" value={form.poManagerApprove} onChange={(v) => handleChange('poManagerApprove', v)} icon={Users} prefix="₦" helperText="Single manager can approve up to this amount" />
 <NumberField label="Director Approval Above (₦)" value={form.poDirectorApprove} onChange={(v) => handleChange('poDirectorApprove', v)} icon={ShieldCheck} prefix="₦" helperText="Requires director-level approval above this" />
 <div className="p-3 bg-amber-50 rounded-xl">
 <p className="text-xs text-amber-700 flex items-center gap-1.5">
 <AlertTriangle size={12} /> POs above ₦{parseInt(form.poDirectorApprove).toLocaleString()} require two approvals (Manager + Director)
 </p>
 </div>
 </div>
 </div>
 </motion.div>
 )}

 {currentStep === 3 && (
 <motion.div key="step3" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
 <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
 <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-red-50/30 to-white ">
 <div className="flex items-center gap-3">
 <div className="p-2 rounded-xl bg-gradient-to-br from-red-50 to-rose-50 ring-1 ring-red-200"><Truck className="w-5 h-5 text-red-600" /></div>
 <div><h3 className="font-semibold text-slate-800">Goods Receiving</h3><p className="text-xs text-slate-500">GRN creation and confirmation rules</p></div>
 </div>
 </div>
 <div className="p-6 space-y-3">
 <ToggleSwitch label="Allow GRN Without PO" description="Receive goods even without a purchase order" checked={form.allowGrnWithoutPO} onChange={(v) => handleToggle('allowGrnWithoutPO', v)} />
 <ToggleSwitch label="Auto-Confirm Inventory on GRN" description="Automatically update stock when goods are received" checked={form.autoConfirmGrn} onChange={(v) => handleToggle('autoConfirmGrn', v)} />
 <ToggleSwitch label="Require GRN Approval" description="Goods received notes need manager verification" checked={form.requireGrnApproval} onChange={(v) => handleToggle('requireGrnApproval', v)} />
 <ToggleSwitch label="Allow Partial GRN" description="Permit receiving partial quantities against a PO" checked={form.allowPartialGrn} onChange={(v) => handleToggle('allowPartialGrn', v)} />
 </div>
 </div>
 </motion.div>
 )}
 </AnimatePresence>

 <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-200 ">
 <button onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))} disabled={currentStep === 1}
 className="flex items-center gap-2 px-5 py-3 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all disabled:opacity-50">
 <ChevronLeft className="w-4 h-4" /> Previous
 </button>
 <span className="text-sm text-slate-500">Step {currentStep} of {STEPS.length}</span>
 <button onClick={() => setCurrentStep(prev => Math.min(STEPS.length, prev + 1))} disabled={currentStep === STEPS.length}
 className="flex items-center gap-2 px-5 py-3 text-sm font-semibold text-white bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 rounded-xl transition-all shadow-lg shadow-rose-500/25 disabled:opacity-50">
 Next <ChevronRight className="w-4 h-4" />
 </button>
 </div>
 </div>
 </div>
 </div>
 </div>
 );
}